import type { FormConfig, Journey, PixelConfig } from '../types';
import { buildUrl, debounce, log, readGA4ClientId } from '../utils';
import { sourceFromUtms, withUtmDetail } from '../sources';
import { getAttribution, getJourneyString, getTimestampString } from '../journey';
import { loadUtms, type StoredUtms } from '../utms';
import { allForms } from './registry';

/**
 * Track which iframes we've already injected, to ensure exactly-once
 * src assignment. Identity by reference, not src string.
 *
 * `let` rather than `const` so clearInjectedTracking() can reassign — WeakSet
 * has no .clear() method, so a fresh WeakSet is the cleanest way to start over.
 */
let injected = new WeakSet<HTMLIFrameElement>();

/**
 * Forget which iframes we've injected, without touching their src. Used on
 * consent transitions so the next inject call sees them as fresh and rewrites
 * src exactly once (rather than blanking to baseUrl first, which would cause
 * a visible flash of the unattributed form).
 */
export function clearInjectedTracking(): void {
  injected = new WeakSet<HTMLIFrameElement>();
}

function resolveSuite(iframe: HTMLIFrameElement, config: PixelConfig): string {
  // Per-iframe data-suite override
  const fromAttr = iframe.dataset.suite;
  if (fromAttr) return fromAttr.toUpperCase();
  // URL param override
  const urlOverride = new URLSearchParams(window.location.search).get(
    'Formstack_Submissions__c.Event_Single_Suite__c'
  );
  if (urlOverride) return urlOverride.toUpperCase();
  return config.defaultSuite;
}

/**
 * Derive a canonical attribution label from stored UTMs, using the same
 * mapping the detector applies to a live URL. Used as a fallback when the
 * journey is empty (e.g., it aged out past validityDays but UTMs are still
 * stored).
 */
function deriveAttributionFromUtms(utms: StoredUtms, config: PixelConfig): string {
  const label = sourceFromUtms(utms.source, utms.medium);
  return label ? withUtmDetail(label, utms, config.attributionDetail) : '';
}

function buildPayload(
  form: FormConfig,
  iframe: HTMLIFrameElement,
  journey: Journey,
  config: PixelConfig
): Record<string, string | undefined> {
  let attribution = getAttribution(journey, config.attributionModel);
  const utms = loadUtms();

  // Fallback: if the journey has no usable attribution, derive one from
  // stored UTMs the same way the detector does on a fresh UTM landing.
  if (!attribution && utms) {
    const derived = deriveAttributionFromUtms(utms, config);
    if (derived) {
      attribution = derived;
      log('attribution fallback from stored UTMs:', derived);
    }
  }

  // Derived values from the journey for the heavier fields.
  const firstTouch = journey.touchpoints[0]?.source;
  const journeyStr = getJourneyString(journey);
  const timestampStr = getTimestampString(journey);
  const ga4Id = readGA4ClientId();
  // Path only — Formstack's WAF rejects URLs that have an `https://` value
  // inside a query parameter (anti-SSRF / open-redirect rule). The path
  // alone identifies which TDG page hosted the form, which is what
  // Salesforce reports need.
  const pageName =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const out: Record<string, string | undefined> = {};
  const f = form.fields;

  if (attribution) out[f.attribution] = attribution;
  if (f.firstTouch && firstTouch) out[f.firstTouch] = firstTouch;
  if (f.journey && journeyStr) out[f.journey] = journeyStr;
  if (f.timestamps && timestampStr) out[f.timestamps] = timestampStr;
  if (f.pageName && pageName) out[f.pageName] = pageName;
  if (f.ga4ClientId && ga4Id) out[f.ga4ClientId] = ga4Id;
  if (f.suite && form.hasSuite) out[f.suite] = resolveSuite(iframe, config);

  if (utms) {
    if (f.utmSource && utms.source) out[f.utmSource] = utms.source;
    if (f.utmMedium && utms.medium) out[f.utmMedium] = utms.medium;
    if (f.utmCampaign && utms.campaign) out[f.utmCampaign] = utms.campaign;
    if (f.utmContent && utms.content) out[f.utmContent] = utms.content;
    if (f.utmTerm && utms.term) out[f.utmTerm] = utms.term;
    if (f.utmId && utms.id) out[f.utmId] = utms.id;
  }

  return out;
}

/**
 * Build the final URL guaranteed to fit within config.urlBudget. Formstack
 * rejects requests past ~2000 chars with the generic "Oops, something went
 * wrong" error, so degrade gracefully instead of breaking the form.
 *
 * Degradation ladder (least → most informational loss):
 *   1. Drop `timestamps` (verbose, lowest analytics value)
 *   2. Truncate journey to first + last 3 with " | ... | " between
 *   3. Drop journey entirely
 *   4. Drop pageName
 * Attribution_Type, First_Touch, UTMs, and Suite are always preserved.
 */
function buildBudgetedUrl(
  form: FormConfig,
  iframe: HTMLIFrameElement,
  journey: Journey,
  config: PixelConfig
): string {
  const payload = buildPayload(form, iframe, journey, config);
  let url = buildUrl(form.baseUrl, payload);
  if (url.length <= config.urlBudget) return url;

  // 1. Drop timestamps
  const tsKey = form.fields.timestamps;
  if (tsKey && payload[tsKey]) {
    delete payload[tsKey];
    url = buildUrl(form.baseUrl, payload);
    log(`URL over budget; dropped timestamps. Length=${url.length}`);
    if (url.length <= config.urlBudget) return url;
  }

  // 2. Truncate journey to first + last 3
  const jKey = form.fields.journey;
  if (jKey && payload[jKey] && journey.touchpoints.length > 4) {
    const tps = journey.touchpoints;
    const first = tps[0].source;
    const lastN = tps.slice(-3).map((t) => t.source);
    payload[jKey] = `${first} | ... | ${lastN.join(' | ')}`;
    url = buildUrl(form.baseUrl, payload);
    log(`URL over budget; truncated journey to first + last 3. Length=${url.length}`);
    if (url.length <= config.urlBudget) return url;
  }

  // 3. Drop journey entirely
  if (jKey && payload[jKey]) {
    delete payload[jKey];
    url = buildUrl(form.baseUrl, payload);
    log(`URL over budget; dropped journey. Length=${url.length}`);
    if (url.length <= config.urlBudget) return url;
  }

  // 4. Drop pageName
  const pnKey = form.fields.pageName;
  if (pnKey && payload[pnKey]) {
    delete payload[pnKey];
    url = buildUrl(form.baseUrl, payload);
    log(`URL over budget; dropped pageName. Length=${url.length}`);
  }

  if (url.length > config.urlBudget) {
    log(`URL still over budget after all degradation. Length=${url.length}`);
  }
  return url;
}

/**
 * Build a consent-denied payload. Carries ONLY the attribution marker
 * "Osano_Blocked" plus the suite value — no UTMs, no journey, no GA4 client
 * ID, no PII of any kind. The user has denied tracking; we still want
 * Salesforce to be able to distinguish these submissions, but we don't
 * record anything beyond the consent state itself.
 */
function buildBlockedPayload(
  form: FormConfig,
  iframe: HTMLIFrameElement,
  config: PixelConfig
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  const f = form.fields;
  out[f.attribution] = 'Osano_Blocked';
  if (f.suite && form.hasSuite) out[f.suite] = resolveSuite(iframe, config);
  return out;
}

/**
 * Inject every known form iframe with the Osano_Blocked marker payload.
 * Used when consent is denied (initial state or transition from granted).
 * Tracked via the same `injected` WeakSet as the normal injection path, so
 * subsequent transitions need to clearInjectedTracking() to re-inject.
 */
export function injectBlockedForms(config: PixelConfig): number {
  let count = 0;
  for (const form of allForms()) {
    const iframes = document.querySelectorAll<HTMLIFrameElement>(form.selector);
    for (const iframe of iframes) {
      if (injected.has(iframe)) continue;
      const payload = buildBlockedPayload(form, iframe, config);
      const url = buildUrl(form.baseUrl, payload);
      injected.add(iframe);
      try {
        iframe.src = url;
        count++;
        log('blocked-injected', form.id, '→', url);
      } catch (e) {
        log('blocked-inject failed for', form.id, e);
        injected.delete(iframe);
      }
    }
  }
  return count;
}

export function injectAllForms(journey: Journey, config: PixelConfig): number {
  let count = 0;
  for (const form of allForms()) {
    const iframes = document.querySelectorAll<HTMLIFrameElement>(form.selector);
    for (const iframe of iframes) {
      if (injected.has(iframe)) continue;
      const finalUrl = buildBudgetedUrl(form, iframe, journey, config);
      // Exactly-once: mark BEFORE assignment so any re-entry skips
      injected.add(iframe);
      try {
        iframe.src = finalUrl;
        count++;
        log('injected', form.id, '→', finalUrl);
      } catch (e) {
        log('inject failed for', form.id, e);
        injected.delete(iframe); // allow retry
      }
    }
  }
  return count;
}

/**
 * Reset all known form iframes back to their base URL with no attribution
 * params, and forget the "already injected" state so a future consent grant
 * can re-inject. Used when consent is revoked mid-session.
 */
export function resetAllForms(): number {
  let count = 0;
  for (const form of allForms()) {
    const iframes = document.querySelectorAll<HTMLIFrameElement>(form.selector);
    for (const iframe of iframes) {
      try {
        // Setting src to the same value would still reload, so check first
        if (iframe.src !== form.baseUrl) {
          iframe.src = form.baseUrl;
          log('reset', form.id, '→', form.baseUrl);
        }
        injected.delete(iframe);
        count++;
      } catch (e) {
        log('reset failed for', form.id, e);
      }
    }
  }
  return count;
}

/**
 * Watch for late-added iframes (CMSes that inject content after DOMContentLoaded).
 * Debounced to coalesce bursts.
 */
export function watchForLateForms(journey: () => Journey, config: PixelConfig): void {
  if (typeof document === 'undefined') return;
  const handle = debounce(() => injectAllForms(journey(), config), 250);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as Element;
        if (el.tagName === 'IFRAME' || el.querySelector?.('iframe')) {
          handle();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

/** For diagnostics — report which iframes are present and whether injected. */
export function listFormState(): { id: string; src: string; injected: boolean }[] {
  const out: { id: string; src: string; injected: boolean }[] = [];
  for (const form of allForms()) {
    const iframes = document.querySelectorAll<HTMLIFrameElement>(form.selector);
    for (const iframe of iframes) {
      out.push({ id: form.id, src: iframe.src, injected: injected.has(iframe) });
    }
  }
  return out;
}
